import type { AgentNameOverrides } from "@repo/app-types/constants/languages";
import type { CompanyPatchPayload } from "@repo/app-types/entities/company";
import { MAX_INTERVIEWER_NAME_LENGTH } from "@repo/app-types/entities/company";
import { SaveAndCancelButtons } from "@repo/ui/Buttons/saveAndCancelButtons";
import { Input } from "@repo/ui/Inputs/input";
import { type FC, useState } from "react";

export interface CompanyInterviewerNamesEditorProps {
  interviewerNames: AgentNameOverrides;
  isDisabled: boolean;
  onSave: (updates: CompanyPatchPayload) => Promise<boolean>;
}

/**
 * The AI interviewer's name for female and male voices. Blank keeps the
 * default (Anna or Daniel).
 */
export const CompanyInterviewerNamesEditor: FC<
  CompanyInterviewerNamesEditorProps
> = ({ interviewerNames, isDisabled, onSave }) => {
  const savedFeminine = interviewerNames.feminine ?? "";
  const savedMasculine = interviewerNames.masculine ?? "";

  const [feminine, setFeminine] = useState(savedFeminine);
  const [masculine, setMasculine] = useState(savedMasculine);

  const hasChanges = feminine !== savedFeminine || masculine !== savedMasculine;

  return (
    <div className="flex flex-col space-y-3">
      <div className="flex flex-wrap gap-4">
        <Input
          label="AI interviewer name (female voices)"
          value={feminine}
          onChange={(evt) => setFeminine(evt.target.value)}
          placeholder="Anna"
          inputClassName="max-w-[200px]"
          isDisabled={isDisabled}
          maxLength={MAX_INTERVIEWER_NAME_LENGTH}
        />
        <Input
          label="AI interviewer name (male voices)"
          value={masculine}
          onChange={(evt) => setMasculine(evt.target.value)}
          placeholder="Daniel"
          inputClassName="max-w-[200px]"
          isDisabled={isDisabled}
          maxLength={MAX_INTERVIEWER_NAME_LENGTH}
        />
      </div>
      <p className="text-sm text-muted-foreground">
        The name the AI interviewer introduces itself with - defaults to Anna or
        Daniel.
      </p>
      {hasChanges ? (
        <SaveAndCancelButtons
          onSave={() => {
            setFeminine(feminine.trim());
            setMasculine(masculine.trim());
            void onSave({
              settings: { interviewer_names: { feminine, masculine } },
            });
          }}
          onCancel={() => {
            setFeminine(savedFeminine);
            setMasculine(savedMasculine);
          }}
        />
      ) : null}
    </div>
  );
};
